/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

// ARIA roles whose elements handle keyboard events internally
const INTERACTIVE_ARIA_ROLES = new Set([
  'combobox', // MUI Select trigger, autocomplete inputs
  'listbox', // Open select dropdown
  'option', // Focused item inside an open listbox
  'spinbutton', // Numeric inputs with up/down arrow navigation
  'slider', // Range/slider controls
]);

/**
 * Returns true if the element is an interactive control where keyboard events
 * have a native meaning (typing, selecting, incrementing)
 */
export const isTargetInteractiveInput = (target: Element | null): boolean => {
  if (!target) {
    return false;
  }
  const el = target as HTMLElement;
  const role = target.getAttribute?.('role');
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    Boolean(el.isContentEditable) ||
    el.getAttribute?.('contenteditable') === 'true' ||
    (role !== null && role !== undefined && INTERACTIVE_ARIA_ROLES.has(role))
  );
};

/**
 * Returns true if keyboard focus is currently inside a floating dialog/panel
 */
export const isDialogFocused = (): boolean =>
  Boolean(document.activeElement?.closest('[role="dialog"]'));
